import {
  AccountPayableAgentModel,
  InvoicePaymentStatusEnum,
  useGetAccountPayableAgent,
} from 'api-hooks/account-payable';
import {
  AccountReceiveableCustomerLiteModel,
  useGetAccountReceivableCustomer,
} from 'api-hooks/account-receivable';
import { ReceiptModel } from 'api-hooks/receipt';
import { Spinner } from 'baseui/spinner';
import { plainToClass } from 'class-transformer';
import stylesColors from 'common/styles/styles.colors';
import { useFormState } from 'components/elements/form';
import IndeterminateCheckbox from 'components/elements/table/table-checkbox';
import TableInput from 'components/elements/table/table-input';
import { ColumnDataType } from 'components/elements/table/type';
import { IColumn } from 'components/elements/table-component/table-component';
import navigateByAcl from 'components/pivot/navigate-by-acl';
import { LinkText } from 'components/pivot/style';
import PurchasePillStatus from 'legacy-modules/order/component/status';
import SalePillStatus from 'legacy-modules/sales/component/status';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

interface Props {
  isFilled: boolean;
  receiptData?: ReceiptModel;
}

interface FormTableProps {
  type: 'payable' | 'receivable' | 'receipt';
}

interface CheckboxHeaderProps extends FormTableProps {
  onChangeExtends?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function CheckboxHeader(props: CheckboxHeaderProps) {
  const { control, setValue } = useFormContext();
  const name = props.type === 'receipt' ? 'receiptDetails' : 'paymentItems';
  const values = useWatch({
    control,
    name,
  });

  const { editable } = useFormState();

  const onClick = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newResult = values.map((value) => {
        return {
          ...value,
          isCheck: event.target.checked,
          ...(props.type === 'receipt'
            ? {}
            : {
                amount: event.target.checked
                  ? value?.total - value?.paidAmount
                  : 0,
              }),
        };
      });
      setValue(name, newResult, { shouldValidate: true });
      props.onChangeExtends?.(event);
    },
    [name, props, setValue, values],
  );

  const isIndeterminate = values.some((value) => value.isCheck);
  const isChecked =
    !!(values || []).length && values.every((value) => !!value.isCheck);

  return (
    <IndeterminateCheckbox
      checked={isChecked}
      indeterminate={isIndeterminate}
      disabled={!editable}
      onChange={onClick}
    />
  );
}

function reducer(
  state,
  action,
  { update, clearErrors, name, replace, setValue },
) {
  switch (action.type) {
    case 'updateCheckboxAll':
      // eslint-disable-next-line no-case-declarations
      const fields = state.data.map((data) => ({
        ...data,
        isCheck: action.checkboxAll,
        amount: action.checkboxAll ? data.total - data.paidAmount : 0,
      }));

      return {
        ...state,
        data: fields,
      };
    case 'update_cell':
      return {
        ...state,
        data: state.data.map((row, index) => {
          if (index === action.rowIndex) {
            const otherVal = () => {
              if (action.columnId === 'isCheck') {
                clearErrors();
                return {
                  amount: action.value ? row.total - row.paidAmount : 0,
                };
              }
            };

            const tempValue = {
              ...state.data[action.rowIndex],
              [action.columnId]: action.value,
              ...otherVal(),
            };

            clearErrors(`${name}.${action.rowIndex}.${action.columnId}`);
            setValue(`${name}.${index}`, tempValue, { shouldValidate: true });
            return tempValue;
          }
          return row;
        }),
      };
    case 'update_all':
      return {
        ...state,
        data: action.newData,
      };
    default:
      return state;
  }
}

const _columns: (props: {
  t: any;
  checkbox?: any;
  editable?: boolean;
  onClickCheckHeader: (event: React.ChangeEvent<HTMLInputElement>) => void;
  type: 'receivable' | 'payable' | 'receipt';
}) => IColumn<any>[] = ({ t, onClickCheckHeader, editable, type }) => {
  return [
    {
      header: () => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <CheckboxHeader type={type} onChangeExtends={onClickCheckHeader} />
          {/* <IndeterminateCheckbox
            {...{
              ...checkbox,
              disabled: !editable,
            }}
          /> */}
        </div>
      ),
      accessorKey: 'isCheck',
      minSize: 50,
      maxSize: 50,
      size: 50,
      dataType: ColumnDataType.checkbox,
    },
    {
      header: t('common:order_number'),
      accessorKey: 'invoiceNumber',
      minSize: 200,
      dataType: ColumnDataType.null,
      disabled: true,
      cell: ({ row }) => {
        if (!row.original.acl) return row.original.invoiceNumber;
        return (
          <LinkText
            href={{
              pathname:
                navigateByAcl[row.original?.acl?.split(/(employee.|.list)/)[2]],
              query: {
                id:
                  row.original.id ||
                  row.original.saleId ||
                  row.original.purchaseId,
              },
            }}
            style={{
              textAlign: 'center',
              display: 'block',
            }}
          >
            {row.original.invoiceNumber}
          </LinkText>
        );
      },
    },
    {
      header: t('common:status'),
      accessorKey: 'status',
      minSize: 150,
      size: 150,
      dataType: ColumnDataType.null,
      disabled: true,
      cell: ({ getValue }) =>
        type === 'payable' ? (
          <PurchasePillStatus value={getValue() as string} />
        ) : (
          <SalePillStatus value={getValue() as string} />
        ),
    },
    {
      header: t('debt:due_date'),
      accessorKey: 'dueDate',
      minSize: 200,
      dataType: ColumnDataType.date,
      disabled: true,
    },
    ...(type === 'receivable'
      ? [
          {
            header: t('common:billing_to'),
            accessorKey: 'billingTo',
            minSize: 150,
            dataType: ColumnDataType.text,
            disabled: true,
          },
        ]
      : []),
    {
      header: t('debt:invoice_total'),
      accessorKey: 'total',
      minSize: 150,
      dataType: ColumnDataType.number,
      minNumber: 0,
      disabled: true,
    },
    {
      header: t('debt:paid_amount'),
      accessorKey: 'paidAmount',
      minSize: 150,
      dataType: ColumnDataType.number,
      minNumber: 0,
      disabled: true,
    },
    {
      header: t('debt:due_amount'),
      accessorKey: 'dueAmount',
      minSize: 150,
      dataType: ColumnDataType.number,
      minNumber: 0,
      disabled: true,
    },
    ...(type === 'payable' || type === 'receivable'
      ? [
          {
            header: t('debt:payment_amount'),
            accessorKey: 'amount',
            minSize: 200,
            dataType: ColumnDataType.number,
            minNumber: 0,
          },
          {
            header: t('debt:pph'),
            accessorKey: 'tax',
            minSize: 150,
            dataType: ColumnDataType.number,
            minNumber: 0,
          },
          {
            header: t('debt:discount'),
            accessorKey: 'discount',
            minSize: 150,
            dataType: ColumnDataType.number,
            minNumber: 0,
          },
          {
            header: t('debt:fee'),
            accessorKey: 'fee',
            minSize: 150,
            dataType: ColumnDataType.number,
            minNumber: 0,
          },
        ]
      : []),
  ];
};

function FormTable(props: FormTableProps) {
  const { type } = props;
  const { t } = useTranslation();
  const { editable } = useFormState();
  const name = type === 'receipt' ? 'receiptDetails' : 'paymentItems';
  // const { current: today } = React.useRef(new Date());
  const {
    control,
    clearErrors,
    formState: { isSubmitting },
    setValue,
  } = useFormContext<any>();

  const { fields, update, replace } = useFieldArray({
    control,
    name,
    keyName: 'customId',
  });

  const [isInitiate, setIsInitiate] = React.useState<{
    value: boolean;
    data: any;
  }>({ value: false, data: undefined });

  const onClickCheckHeader = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: 'updateCheckboxAll',
        checkboxAll: event.target.checked,
      });
    },
    [],
  );

  const columns = React.useCallback(() => {
    return _columns({
      t,
      editable,
      onClickCheckHeader,
      type,
    });
  }, [editable, onClickCheckHeader, t, type]);

  const [state, dispatch] = React.useReducer(
    (state, action) =>
      reducer(state, action, {
        update,
        clearErrors,
        name,
        replace,
        setValue,
      }),
    {
      columns: columns(),
      data: [],
    },
  );

  React.useEffect(() => {
    if (!isInitiate.value && fields.length) {
      dispatch({
        type: 'update_all',
        newData: fields.length ? fields : [],
      });
      setIsInitiate({
        value: true,
        data: fields.length ? fields : [],
      });
    }
  }, [fields, isInitiate]);

  React.useEffect(() => {
    if (!editable && isInitiate.value && !isSubmitting) {
      dispatch({ type: 'update_all', newData: isInitiate.data });
    }
  }, [editable, isInitiate.value, isSubmitting, t, isInitiate.data]);

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        name="paymentItems"
        disabled={!editable}
        columns={columns() as any}
        data={state.data}
        dispatch={dispatch}
        uniqueRowKey="customId"
        noAddRemoveRow
        onCustomDisabledCell={({ accessorKey, rowOriginal }) => {
          if (
            (accessorKey === 'amount' ||
              accessorKey === 'tax' ||
              accessorKey === 'discount' ||
              accessorKey === 'fee') &&
            !rowOriginal.isCheck
          ) {
            return true;
          }
          return false;
        }}
      />
    </div>
  );
}

export function AccountPayableTableForm(props: Props) {
  const { control, setValue, getValues } = useFormContext();
  const receiptData = props.receiptData;
  const { editable } = useFormState();

  const [values] = useWatch({
    control,
    name: ['agentId'],
  });

  const { isFetching } = useGetAccountPayableAgent(
    {
      id: values,
      othersKeys: ['account-payable', receiptData ? 'with-receipt-data' : ''],
      params: {
        payment_status: InvoicePaymentStatusEnum.pending,
      },
    },
    {
      enabled: !!values && editable && !props.isFilled,
      onSuccess(data) {
        const result = data.data.map((item) =>
          plainToClass(AccountPayableAgentModel, item),
        );

        const oldData = getValues('paymentItems');
        const newResult = result.map((value) => {
          const currentChecked = (oldData || []).find(
            (data) => data.id === value.id,
          );
          return {
            ...value,
            purchaseId: value.id,
            purchaseRefId: value.refId,
            isCheck: currentChecked?.isCheck ?? false,
            amount: currentChecked?.amount ?? 0,
            tax: currentChecked?.tax ?? 0,
            discount: currentChecked?.discount ?? 0,
            fee: currentChecked?.fee ?? 0,
            dueAmount:
              currentChecked?.dueAmount ?? value.total - value.paidAmount,
          };
        });

        setValue('paymentItems', newResult);
      },
    },
  );

  return (
    <>
      {isFetching ? (
        <div style={spinerStyle}>
          <Spinner $color={stylesColors.primaryDefault} />
        </div>
      ) : (
        <FormTable type="payable" key={`${values}-payable`} />
      )}
    </>
  );
}

export function AccountReceivableTableForm(props: Props) {
  const { control, setValue, getValues } = useFormContext();

  const receiptData = props.receiptData;
  const { editable } = useFormState();

  const [values] = useWatch({
    control,
    name: ['customerId'],
  });

  const { isFetching } = useGetAccountReceivableCustomer(
    {
      id: values,
      othersKeys: [
        'account-receivable',
        receiptData ? 'with-receipt-data' : '',
      ],
      params: {
        payment_status: InvoicePaymentStatusEnum.pending,
      },
    },
    {
      enabled: !!values && editable && !props.isFilled,
      onSuccess(data) {
        const result = data.data.map((item) =>
          plainToClass(AccountReceiveableCustomerLiteModel, item),
        );

        const oldData = getValues('paymentItems');

        const newResult = result.map((value) => {
          const currentChecked = (oldData || []).find(
            (data) => data.saleId === value.id,
          );
          const defaultTemp = {
            ...value,
            invoiceNumber: value.invoiceNumber,
            status: value.status,
            dueDate: value.dueDate,
            total: value.total,
            paidAmount: value.paidAmount,
            billingTo: value.billingTo,

            saleId: value.id,
            saleRefId: value.refId,
            amount: currentChecked?.amount ?? 0,
            tax: currentChecked?.tax ?? 0,
            discount: currentChecked?.discount ?? 0,
            fee: currentChecked?.fee ?? 0,
            dueAmount:
              currentChecked?.dueAmount ?? value.total - value.paidAmount,
          };

          const foundData = receiptData?.receiptDetails.find(
            (curr) => curr.sale.id === value.id,
          );

          return {
            ...defaultTemp,
            isCheck: currentChecked?.isCheck ?? foundData ?? false,
            amount:
              currentChecked?.amount ?? foundData ? defaultTemp.dueAmount : 0,
          };
        });

        setValue('paymentItems', newResult, {
          shouldValidate: true,
          shouldTouch: true,
        });
      },
    },
  );

  return (
    <>
      {isFetching ? (
        <div style={spinerStyle}>
          <Spinner $color={stylesColors.primaryDefault} />
        </div>
      ) : (
        <FormTable type="receivable" key={`${values}-receiveable`} />
      )}
    </>
  );
}

export function ReceiptTableForm(props: Props) {
  const { control, setValue, getValues } = useFormContext();
  const { editable } = useFormState();

  const [values] = useWatch({
    control,
    name: ['customerId'],
  });

  const { isFetching } = useGetAccountReceivableCustomer(
    { id: values, params: { receipt_in_use: true } },
    {
      enabled: !!values && editable && !props.isFilled,
      onSuccess(data) {
        const result = data.data.map((item) =>
          plainToClass(AccountReceiveableCustomerLiteModel, item),
        );

        const oldData = getValues('receiptDetails');

        const newResult = result.map((value) => {
          const currentData = (oldData || []).find(
            (data) => data?.saleId === value.id,
          );
          return {
            ...value,
            id: undefined,
            saleId: value.id,
            saleRefId: value.refId,
            dueAmount: currentData?.dueAmount ?? value.total - value.paidAmount,
            isCheck: currentData?.dueAmount ?? false,
          };
        });

        setValue('receiptDetails', newResult);
      },
    },
  );

  const [isShow, setIsShow] = React.useState(true);
  React.useEffect(() => {
    if (!values) {
      setIsShow(false);
      setTimeout(() => {
        setIsShow(true);
      }, 100);
    }
  }, [values]);

  if (!isShow) {
    return null;
  }

  return (
    <>
      {isFetching ? (
        <div style={spinerStyle}>
          <Spinner $color={stylesColors.primaryDefault} />
        </div>
      ) : (
        <FormTable type="receipt" />
      )}
    </>
  );
}

const spinerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};
